import type { Decor, Rect } from '../world/mapdef';
import { shoreX } from '../world/maps/hualien';
import { T } from '../world/tiles';
import { block, ellipse, rect, shadow, shade, type Ctx } from './paint';
import { wind } from './nature';
import { registerPropDrawers } from './propRegistry';
import { drawCharacter, type CharLook, type Pose } from './sprites';
import { isNight, line, poly, sign, stall, steam, txt } from './propsHualien';

/**
 * Taroko Gorge and Qixingtan (taroko package): the Eternal Spring Shrine and its waterfall, the bell
 * tower, Cimu Bridge, Swallow Grotto's pocked cliff and its swallows, rock-fall nets, the Truku terrace
 * at Buluowan, Formosan rock monkeys, Shakadang's red bridge and marble boulders, coaches and the
 * shuttle stop, Qixingtan's surf, boats, cyclists, the Dawn Bridge and the star-gazing deck.
 */

const RED = '#c0392b';
const RED_L = '#e05a48';
const RED_D = '#8a2a1e';
const TRUKU = ['#c0392b', '#1b1d24', '#f4f1ea'];

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

/** Chinese hip roof with upswept eaves, from `l` to `r`, eave line at `y`, ridge `h` above. */
function hipRoof(c: Ctx, l: number, r: number, y: number, h: number, tile: string, ridge: string): void {
  const w = r - l, cx = (l + r) / 2;
  c.fillStyle = shade(tile, -0.35);
  c.beginPath();
  c.moveTo(l - 4, y - 4);
  c.quadraticCurveTo(l + w * 0.12, y + 1.5, l + w * 0.3, y + 1);
  c.lineTo(r - w * 0.3, y + 1);
  c.quadraticCurveTo(r - w * 0.12, y + 1.5, r + 4, y - 4);
  c.lineTo(r - w * 0.16, y - h);
  c.lineTo(l + w * 0.16, y - h);
  c.closePath();
  c.fill();
  c.fillStyle = tile;
  c.beginPath();
  c.moveTo(l - 2.5, y - 4);
  c.quadraticCurveTo(l + w * 0.12, y, l + w * 0.3, y - 0.4);
  c.lineTo(r - w * 0.3, y - 0.4);
  c.quadraticCurveTo(r - w * 0.12, y, r + 2.5, y - 4);
  c.lineTo(r - w * 0.17, y - h + 1);
  c.lineTo(l + w * 0.17, y - h + 1);
  c.closePath();
  c.fill();
  // Tile courses.
  for (let i = l + 2; i < r - 1; i += 2.4) {
    const k = Math.abs(i - cx) / (w / 2);
    rect(c, 'rgba(60,20,10,0.28)', i, y - h + 1.5 + k * 1.5, 0.5, h - 3 - k * 2);
  }
  rect(c, 'rgba(255,255,255,0.18)', l + w * 0.18, y - h + 1.5, w * 0.64, 0.6);
  block(c, l + w * 0.15, y - h - 2.4, w * 0.7, 3, ridge, shade(ridge, 0.3));
  for (const s of [-1, 1]) {
    const ex = s < 0 ? l + w * 0.15 : r - w * 0.15;
    poly(c, ridge, [ex, y - h - 2.4, ex + s * 3.6, y - h - 5.4, ex + s * 1.4, y - h + 0.4]);
  }
}

/** Truku woven band: red / black / white diamonds (the “eyes of the ancestors”). */
function trukuBand(c: Ctx, x: number, y: number, w: number, h: number): void {
  rect(c, TRUKU[0], x, y, w, h);
  const s = h * 0.9;
  for (let i = x + s / 2; i < x + w - s / 2 + 0.1; i += s) {
    poly(c, TRUKU[2], [i, y + 0.3, i + s / 2 - 0.2, y + h / 2, i, y + h - 0.3, i - s / 2 + 0.2, y + h / 2]);
    poly(c, TRUKU[1], [i, y + h * 0.25, i + s / 4, y + h / 2, i, y + h * 0.75, i - s / 4, y + h / 2]);
  }
}

/** Marble boulder: white with grey veins and a mossy foot. */
function boulder(c: Ctx, cx: number, by: number, rw: number, rh: number, wet = false): void {
  if (wet) {
    ellipse(c, 'rgba(240,255,252,0.75)', cx, by - 0.5, rw + 3, rh * 0.45 + 1.5);
  } else shadow(c, cx, by, rw + 1, 2);
  c.fillStyle = '#8a867c';
  c.beginPath();
  c.ellipse(cx, by - rh * 0.5, rw, rh * 0.62, 0, Math.PI, 0);
  c.lineTo(cx + rw, by - 0.4);
  c.lineTo(cx - rw, by - 0.4);
  c.fill();
  c.fillStyle = '#e8e6e0';
  c.beginPath();
  c.ellipse(cx - 0.4, by - rh * 0.55, rw - 0.8, rh * 0.55, 0, Math.PI, 0);
  c.lineTo(cx + rw - 0.8, by - 1.2);
  c.lineTo(cx - rw + 0.4, by - 1.2);
  c.fill();
  ellipse(c, 'rgba(255,255,255,0.8)', cx - rw * 0.35, by - rh * 0.85, rw * 0.35, rh * 0.18);
  line(c, 'rgba(110,110,118,0.55)', 0.5, [cx - rw * 0.6, by - rh * 0.3, cx - rw * 0.1, by - rh * 0.7, cx + rw * 0.5, by - rh * 0.5]);
  line(c, 'rgba(110,110,118,0.35)', 0.4, [cx + rw * 0.1, by - rh * 0.2, cx + rw * 0.4, by - rh * 0.9]);
  if (!wet) rect(c, 'rgba(80,130,60,0.5)', cx - rw + 1, by - 1.6, rw * 2 - 2, 1);
}

/** A falling-rock warning: red-bordered triangle with tumbling rocks, and a yellow plate. */
function rockWarning(c: Ctx, cx: number, by: number): void {
  shadow(c, cx, by, 4, 1.4);
  rect(c, '#55606a', cx - 0.7, by - 22, 1.4, 22);
  poly(c, RED, [cx - 7, by - 15, cx + 7, by - 15, cx, by - 27]);
  poly(c, '#fbf8ef', [cx - 4.8, by - 16.4, cx + 4.8, by - 16.4, cx, by - 24.6]);
  poly(c, '#1b1d24', [cx - 3.6, by - 17, cx + 2, by - 17, cx - 2, by - 22]);
  ellipse(c, '#1b1d24', cx + 1.6, by - 20.4, 1, 1);
  ellipse(c, '#1b1d24', cx + 2.8, by - 18.4, 0.8, 0.8);
  block(c, cx - 7, by - 14, 14, 6, '#ffd34e', '#fff0a0', '#c8a020');
  txt(c, '注意落石', cx, by - 10.9, 3, '#1b1d24', 900);
}

/** Small Taiwanese fishing boat seen from the side. */
function boat(c: Ctx, cx: number, wl: number, s: number, hull: string, stripe: string, t: number, flag = true): void {
  c.fillStyle = hull;
  c.beginPath();
  c.moveTo(cx - 20 * s, wl - 7 * s);
  c.lineTo(cx + 22 * s, wl - 8 * s);
  c.quadraticCurveTo(cx + 18 * s, wl + 1 * s, cx + 10 * s, wl + 1.6 * s);
  c.lineTo(cx - 15 * s, wl + 1.6 * s);
  c.quadraticCurveTo(cx - 20 * s, wl - 1 * s, cx - 20 * s, wl - 7 * s);
  c.fill();
  rect(c, stripe, cx - 19 * s, wl - 4 * s, 38 * s, 1.4 * s);
  rect(c, 'rgba(0,0,0,0.25)', cx - 15 * s, wl + 0.4 * s, 26 * s, 1.2 * s);
  rect(c, '#ffffff', cx - 20 * s, wl - 7.6 * s, 42 * s, 0.9 * s);
  // Wheelhouse, mast with a string of squid-fishing lamps.
  block(c, cx - 2 * s, wl - 16 * s, 11 * s, 8.4 * s, '#f4f5f2', '#ffffff', '#c8ccd0');
  rect(c, '#3a5a70', cx + 0 * s, wl - 14 * s, 3 * s, 3 * s);
  rect(c, '#3a5a70', cx + 4.6 * s, wl - 14 * s, 3 * s, 3 * s);
  block(c, cx - 3 * s, wl - 17.6 * s, 13 * s, 2 * s, stripe);
  line(c, '#55606a', 0.6, [cx - 10 * s, wl - 7 * s, cx - 10 * s, wl - 26 * s]);
  line(c, 'rgba(60,60,60,0.6)', 0.4, [cx - 10 * s, wl - 25 * s, cx + 20 * s, wl - 8 * s]);
  line(c, 'rgba(60,60,60,0.6)', 0.4, [cx - 10 * s, wl - 25 * s, cx - 19 * s, wl - 7 * s]);
  const lit = isNight();
  for (let i = 1; i < 6; i++) {
    const k = i / 6;
    ellipse(c, lit ? '#fff3b0' : '#e8e4d8', cx - 10 * s + 30 * s * k, wl - 25 * s + 17 * s * k, 0.9, 0.9);
    if (lit) ellipse(c, 'rgba(255,240,170,0.25)', cx - 10 * s + 30 * s * k, wl - 25 * s + 17 * s * k, 3, 3);
  }
  if (flag) {
    const f = Math.sin(t * 4 + cx) * 0.8;
    poly(c, RED, [cx - 10 * s, wl - 26 * s, cx - 4 * s, wl - 25 * s + f, cx - 10 * s, wl - 23 * s]);
  }
}

// ---------------------------------------------------------------------------
// Objects
// ---------------------------------------------------------------------------

function shuttleStop(c: Ctx, r: Rect, name: string): void {
  const by = r.y + r.h, cx = r.x + r.w / 2;
  shadow(c, cx, by, r.w / 2 + 4, 2.6);
  // A little shelter: two posts, a curved teal roof, a bench.
  for (const px of [r.x - 1, r.x + r.w - 1]) block(c, px, by - 30, 2.4, 30, '#5a6470', '#7a8490');
  c.fillStyle = '#1f7a6a';
  c.beginPath();
  c.moveTo(r.x - 5, by - 29);
  c.quadraticCurveTo(cx, by - 37, r.x + r.w + 5, by - 29);
  c.lineTo(r.x + r.w + 5, by - 31);
  c.quadraticCurveTo(cx, by - 39, r.x - 5, by - 31);
  c.fill();
  rect(c, 'rgba(255,255,255,0.35)', r.x - 2, by - 33, r.w + 4, 0.6);
  block(c, r.x + 3, by - 9, r.w - 6, 3, '#a8784a', '#c8986a');
  for (const px of [r.x + 5, r.x + r.w - 7]) rect(c, '#5a6470', px, by - 6, 1.4, 6);
  // Timetable panel inside the shelter.
  block(c, r.x + 4, by - 26, 10, 12, '#fbf8ef', '#ffffff');
  for (let i = 0; i < 4; i++) rect(c, '#55606a', r.x + 5.5, by - 23.5 + i * 2.4, 7, 0.6);
  // The 台灣好行 flag sign on its own pole.
  const sx = r.x + r.w - 6;
  rect(c, '#3a3e44', sx, by - 46, 1.6, 46);
  block(c, sx - 10, by - 52, 22, 16, '#ffffff', '#ffffff', '#c8ccd0');
  block(c, sx - 10, by - 52, 22, 6, '#2f9a5a', '#4ab874');
  txt(c, '台灣好行', sx + 1, by - 49, 3.8, '#ffffff', 900);
  txt(c, name, sx + 1, by - 43.4, 4.2, '#1f6a40', 900);
  txt(c, 'Tourist Shuttle', sx + 1, by - 39.2, 2, '#55606a', 700);
  // A tiny bus icon.
  block(c, r.x + 16, by - 25, 7, 4, '#2f9a5a');
  rect(c, '#bfe6ff', r.x + 17, by - 24.4, 5, 1.2);
}

function visitorDoors(c: Ctx, r: Rect): void {
  const cx = r.x + r.w / 2, wallBottom = r.y + r.h - 16 + 0;
  // Glass double doors set into the facade, with a canopy.
  block(c, cx - 13, wallBottom - 24, 26, 24, '#5a6470', '#7a8490');
  for (const dx of [-11.4, 0.4]) {
    block(c, cx + dx, wallBottom - 22, 11, 22, '#7fb8d8', '#bfe6ff', '#4a7a98');
    rect(c, 'rgba(255,255,255,0.45)', cx + dx + 2, wallBottom - 20, 1.4, 16);
    rect(c, '#3a3e44', cx + dx + (dx < 0 ? 9 : 1), wallBottom - 12, 0.8, 4);
  }
  block(c, cx - 17, wallBottom - 28, 34, 4.5, '#2f6a4a', '#4a8a6a', '#1f4a34');
  // Welcome mat and an “i” info stand.
  rect(c, '#55606a', cx - 10, wallBottom + 1, 20, 4);
  rect(c, '#7a8490', cx - 9, wallBottom + 2, 18, 0.6);
  const ix = r.x + r.w + 4, iy = r.y + r.h;
  shadow(c, ix, iy, 4, 1.4);
  rect(c, '#3a3e44', ix - 0.7, iy - 16, 1.4, 16);
  block(c, ix - 5, iy - 24, 10, 10, '#2f6fa8', '#4a8ac8');
  txt(c, 'i', ix, iy - 19, 7, '#ffffff', 900);
}

function cimuPavilion(c: Ctx, r: Rect): void {
  // 慈母亭: a red pavilion on a marble outcrop above the river, green glazed roof.
  const cx = r.x + r.w / 2, by = r.y + r.h;
  shadow(c, cx, by, r.w / 2 + 2, 3);
  boulder(c, cx, by + 1, r.w / 2 + 2, 14);
  block(c, cx - 18, by - 12, 36, 4, '#d8d4ca', '#ecebe4', '#a8a49a');
  for (let i = 0; i < 3; i++) rect(c, '#f2efe6', cx - 5 + i, by - 8 + i * 2, 10 - i * 2, 0.8);
  for (const px of [cx - 15, cx - 6, cx + 4, cx + 13]) block(c, px, by - 34, 2.6, 22, RED, RED_L, RED_D);
  // Railing between the columns.
  rect(c, RED_D, cx - 15, by - 18, 30, 1.2);
  for (let i = cx - 14; i < cx + 15; i += 3) rect(c, RED, i, by - 18, 0.8, 5);
  rect(c, '#2f6a5a', cx - 17, by - 37, 34, 2.6);
  for (let i = cx - 16; i < cx + 16; i += 3) rect(c, '#d9b450', i, by - 36.4, 1, 1);
  hipRoof(c, cx - 19, cx + 19, by - 37, 10, '#3f9a6a', '#2f6a4a');
  hipRoof(c, cx - 11, cx + 11, by - 49, 7, '#3f9a6a', '#2f6a4a');
  rect(c, RED, cx - 10, by - 49, 20, 2);
  ellipse(c, '#ffd34e', cx, by - 60, 1.6, 1.6);
  block(c, cx - 7, by - 32, 14, 6, '#1f4a7a', '#2f5f98');
  txt(c, '慈母亭', cx, by - 28.8, 4, '#ffd34e', 900);
}

function shrine(c: Ctx, r: Rect, t: number): void {
  // 長春祠: vermilion pavilion built into the marble cliff, red tiled roof, the spring gushing out below.
  const cx = r.x + r.w / 2, by = r.y + r.h;
  // Cliff rock behind and under the shrine.
  c.fillStyle = '#b8b4aa';
  c.beginPath();
  c.moveTo(r.x - 8, by + 2);
  c.lineTo(r.x - 6, by - 44);
  c.lineTo(r.x + 6, by - 58);
  c.lineTo(r.x + r.w - 4, by - 60);
  c.lineTo(r.x + r.w + 8, by - 40);
  c.lineTo(r.x + r.w + 9, by + 2);
  c.fill();
  for (let i = 0; i < 9; i++) rect(c, i % 2 ? 'rgba(255,255,255,0.35)' : 'rgba(60,55,50,0.2)', r.x - 4 + i * 10, by - 50 + (i % 3) * 4, 0.8, 30 + (i % 3) * 8);
  ellipse(c, '#4f8a3f', r.x - 3, by - 44, 5, 2.2);
  ellipse(c, '#5f9a45', r.x + r.w + 4, by - 38, 4, 2);
  // The spring: white water pouring from two arches in the base, down into the river.
  const top = by - 4, bot = by + 42;
  for (const wx of [cx - 11, cx + 6]) {
    rect(c, '#2a3a40', wx - 1, by - 9, 7, 6);
    c.fillStyle = 'rgba(232,248,250,0.92)';
    c.beginPath();
    c.moveTo(wx, top);
    c.lineTo(wx + 5, top);
    c.lineTo(wx + 8, bot);
    c.lineTo(wx - 4, bot);
    c.fill();
    for (let k = 0; k < 4; k++) {
      const u = (t * 1.6 + k / 4 + wx * 0.01) % 1;
      rect(c, 'rgba(160,210,220,0.7)', wx - 2 + k * 2.6, top + u * (bot - top), 1, 5);
    }
  }
  // Foam and mist where it hits the river.
  for (let k = 0; k < 5; k++) {
    const u = (t * 0.8 + k / 5) % 1;
    ellipse(c, `rgba(255,255,255,${0.7 * (1 - u)})`, cx - 12 + k * 6, bot - u * 4, 3 + u * 4, 1.6 + u * 1.4);
  }
  ellipse(c, 'rgba(255,255,255,0.35)', cx, bot - 6, 20, 5);
  // Stone platform.
  block(c, r.x - 2, by - 10, r.w + 4, 8, '#d8d4ca', '#ecebe4', '#a8a49a');
  for (let i = r.x; i < r.x + r.w; i += 6) rect(c, 'rgba(0,0,0,0.12)', i, by - 9, 0.5, 6);
  // White walls and red columns, a gilded name board.
  block(c, cx - 26, by - 34, 52, 24, '#f2ede2', '#ffffff', '#c8c0b0');
  for (const px of [cx - 26, cx - 15, cx - 4, cx + 7, cx + 18]) block(c, px, by - 35, 3, 25, RED, RED_L, RED_D);
  rect(c, '#3a2418', cx - 3, by - 30, 6, 20);
  for (const wx of [cx - 12, cx + 9]) {
    rect(c, '#5a3a24', wx, by - 28, 5, 9);
    for (let k = 0; k < 3; k++) rect(c, '#c8a070', wx + 0.8 + k * 1.6, by - 27.4, 0.4, 7.8);
  }
  rect(c, '#2f6a5a', cx - 28, by - 38, 56, 3);
  for (let i = cx - 27; i < cx + 27; i += 3) rect(c, '#d9b450', i, by - 37.4, 1, 1.2);
  // Two-tier red roof.
  hipRoof(c, cx - 31, cx + 31, by - 38, 12, '#d2502e', '#8a2a1e');
  block(c, cx - 14, by - 54, 28, 5, '#f2ede2', '#ffffff');
  for (const px of [cx - 14, cx - 5, cx + 4, cx + 11]) rect(c, RED, px, by - 54, 2.4, 5);
  hipRoof(c, cx - 17, cx + 17, by - 54, 8, '#d2502e', '#8a2a1e');
  ellipse(c, '#ffd34e', cx, by - 66, 1.6, 1.6);
  block(c, cx - 9, by - 47, 18, 6, '#1f4a7a', '#2f5f98');
  txt(c, '長春祠', cx, by - 43.8, 4.2, '#ffd34e', 900);
  if (isNight()) ellipse(c, 'rgba(255,200,120,0.18)', cx, by - 24, 30, 14);
}

function bellTower(c: Ctx, r: Rect, t: number): void {
  const cx = r.x + r.w / 2, by = r.y + r.h;
  shadow(c, cx, by, 16, 3);
  block(c, cx - 15, by - 6, 30, 6, '#b8b4aa', '#d8d4ca', '#8a867c');
  for (const px of [cx - 13, cx + 10]) block(c, px, by - 38, 3, 32, RED, RED_L, RED_D);
  rect(c, RED_D, cx - 13, by - 36, 26, 2);
  // The bronze bell with its striking log.
  const sw = Math.sin(t * 1.2) * 0.4;
  rect(c, '#3a2418', cx - 0.5, by - 36, 1, 4);
  c.fillStyle = '#6a5a2a';
  c.beginPath();
  c.moveTo(cx - 5 + sw, by - 32);
  c.quadraticCurveTo(cx - 6 + sw, by - 19, cx - 7.5 + sw, by - 17);
  c.lineTo(cx + 7.5 + sw, by - 17);
  c.quadraticCurveTo(cx + 6 + sw, by - 19, cx + 5 + sw, by - 32);
  c.closePath();
  c.fill();
  rect(c, '#8a7a3a', cx - 6 + sw, by - 20, 12, 1);
  rect(c, '#8a7a3a', cx - 5 + sw, by - 27, 10, 0.8);
  rect(c, 'rgba(255,240,180,0.35)', cx - 3 + sw, by - 30, 1.2, 10);
  for (let i = 0; i < 3; i++) for (let j = 0; j < 2; j++) ellipse(c, '#a8984a', cx - 2.4 + i * 2.4 + sw, by - 25 + j * 2, 0.5, 0.5);
  line(c, '#3a2418', 0.5, [cx + 13, by - 30, cx + 9, by - 26]);
  block(c, cx + 7, by - 27.6, 7, 2.4, '#8a5a34', '#a8784a');
  hipRoof(c, cx - 18, cx + 18, by - 38, 11, '#d2502e', '#8a2a1e');
  ellipse(c, '#ffd34e', cx, by - 52, 1.4, 1.4);
}

function trailBoard(c: Ctx, r: Rect, title: string, en: string, art: (x: number, y: number) => void): void {
  const by = r.y + r.h, cx = r.x + r.w / 2;
  shadow(c, cx, by, 13, 2.2);
  for (const px of [cx - 11, cx + 8.6]) block(c, px, by - 18, 2.4, 18, '#5a3a20', '#7a5030');
  block(c, cx - 15, by - 36, 30, 20, '#6a4428', '#8a5a34', '#4a2e18');
  poly(c, '#3a5a3a', [cx - 18, by - 35, cx + 18, by - 35, cx + 13, by - 40, cx - 13, by - 40]);
  txt(c, title, cx, by - 32, 4.8, '#fff3d0', 900, 'rgba(0,0,0,0.4)');
  txt(c, en, cx, by - 28.6, 2, '#f2dcb0', 700);
  block(c, cx - 12.4, by - 27, 24.8, 9.4, '#e8e0cc', '#f6f0e0');
  art(cx, by - 22);
}

function swallowBoard(c: Ctx, r: Rect): void {
  trailBoard(c, r, '燕子口', 'SWALLOW GROTTO', (x, y) => {
    rect(c, '#b8b4aa', x - 12, y - 4.6, 24, 9);
    for (let i = 0; i < 6; i++) ellipse(c, '#4a4640', x - 9 + i * 3.6, y - 2 + (i % 2) * 3, 1, 0.8);
    poly(c, '#1b2a4a', [x - 2, y, x + 3, y - 2, x + 1, y + 0.4, x + 5, y + 1.6, x + 1, y + 1.4]);
    rect(c, '#9ad0d8', x - 12, y + 3, 24, 1.4);
  });
}

function shakadangBoard(c: Ctx, r: Rect): void {
  trailBoard(c, r, '砂卡礑步道', 'SHAKADANG TRAIL', (x, y) => {
    rect(c, '#3fc8c0', x - 12, y - 1, 24, 3);
    for (let i = 0; i < 4; i++) ellipse(c, '#f4f2ec', x - 9 + i * 6, y + 0.4, 1.4, 0.8);
    line(c, '#8a5a34', 0.6, [x - 12, y - 3, x - 4, y - 4, x + 4, y - 3, x + 12, y - 4]);
  });
}

function buluowanSign(c: Ctx, r: Rect): void {
  const by = r.y + r.h, cx = r.x + r.w / 2;
  shadow(c, cx, by, 14, 2.2);
  for (const px of [cx - 13, cx + 10]) {
    block(c, px, by - 26, 3.4, 26, '#6a4a2a', '#8a6440');
    ellipse(c, '#8a6440', px + 1.7, by - 26, 1.7, 0.8);
  }
  block(c, cx - 15, by - 25, 30, 12, '#8a5a34', '#a8784a', '#5a3a20');
  trukuBand(c, cx - 15, by - 26.6, 30, 2.6);
  trukuBand(c, cx - 15, by - 14.6, 30, 2.6);
  txt(c, '布洛灣', cx, by - 20.6, 5, '#fff3d0', 900, 'rgba(0,0,0,0.4)');
  txt(c, 'BULUOWAN', cx, by - 16.6, 2, '#f2dcb0', 700);
}

function loom(c: Ctx, r: Rect): void {
  // A Truku backstrap loom: the warp tied to a post, red-white cloth growing toward the weaver.
  const by = r.y + r.h, x = r.x, w = r.w;
  shadow(c, x + w / 2, by, w / 2 + 2, 2.4);
  block(c, x + w - 5, by - 26, 3.4, 26, '#6a4a2a', '#8a6440');
  rect(c, '#5a3a20', x + 2, by - 9, w - 6, 1.6);
  for (let i = 0; i < 9; i++) line(c, i % 3 === 0 ? RED : '#f4f1ea', 0.4, [x + 4 + i * 1.2, by - 9, x + w - 4, by - 22 + i * 0.6]);
  // Woven part: the diamond pattern.
  c.save();
  c.translate(x + 3, by - 12);
  c.rotate(-0.42);
  trukuBand(c, 0, 0, 13, 5);
  c.restore();
  block(c, x + 1, by - 6, 8, 6, '#a8784a', '#c8986a'); // low stool
  // Finished cloths folded in a basket.
  block(c, x - 8, by - 7, 9, 7, '#c8a070', '#e0bc8c', '#8a6a40');
  trukuBand(c, x - 7.4, by - 9, 7.8, 2.4);
}

function monkeySign(c: Ctx, r: Rect): void {
  const by = r.y + r.h, cx = r.x + r.w / 2;
  shadow(c, cx, by, 5, 1.4);
  rect(c, '#55606a', cx - 0.7, by - 18, 1.4, 18);
  block(c, cx - 9, by - 30, 18, 14, '#fbf8ef', '#ffffff', '#c8ccd0');
  // Monkey face.
  ellipse(c, '#8a6a4a', cx - 3, by - 24.6, 3.4, 3.2);
  ellipse(c, '#e8a890', cx - 3, by - 24, 2.2, 2);
  rect(c, '#1b1d24', cx - 4, by - 25, 0.6, 0.6);
  rect(c, '#1b1d24', cx - 2.4, by - 25, 0.6, 0.6);
  // A banana with a red “no” slash.
  c.strokeStyle = '#ffd34e';
  c.lineWidth = 1.2;
  c.beginPath();
  c.arc(cx + 4, by - 26, 2.6, 0.3, 2.2);
  c.stroke();
  c.strokeStyle = RED;
  c.lineWidth = 0.8;
  c.beginPath();
  c.arc(cx + 4, by - 24.4, 3.6, 0, Math.PI * 2);
  c.stroke();
  line(c, RED, 0.8, [cx + 1.6, by - 22, cx + 6.4, by - 26.8]);
  txt(c, '請勿餵食', cx, by - 18.6, 3, RED, 900);
}

function bikeRent(c: Ctx, r: Rect): void {
  const by = r.y + r.h, x = r.x, w = r.w, cx = x + w / 2;
  shadow(c, cx, by, w / 2 + 3, 2.6);
  block(c, x + 2, by - 22, w - 4, 22, '#f4f1ea', '#ffffff', '#c8c4b8');
  block(c, x + 5, by - 18, w - 10, 8, '#3a5a70', '#4a6a80');
  rect(c, 'rgba(180,220,255,0.3)', x + 6, by - 17, 3, 6);
  poly(c, '#3ab0a0', [x - 2, by - 22, x + w + 2, by - 22, x + w - 1, by - 27, x + 1, by - 27]);
  for (let i = 0; i < w; i += 4) rect(c, '#ffffff', x + i, by - 26.6, 2, 4);
  sign(c, '租車', cx, by - 38, 22, '#2f6fa8');
  // Two bikes leaning in front.
  for (let k = 0; k < 2; k++) {
    const bx = x + 6 + k * 12, wy = by - 2.6;
    c.strokeStyle = '#1b1d24';
    c.lineWidth = 0.7;
    for (const wx of [bx, bx + 7]) {
      c.beginPath();
      c.arc(wx, wy, 2.4, 0, Math.PI * 2);
      c.stroke();
    }
    line(c, k ? '#e2493b' : '#3a8fd9', 0.9, [bx, wy, bx + 3, wy - 3.4, bx + 7, wy, bx + 3.4, wy, bx + 3, wy - 3.4, bx + 6, wy - 3.6]);
    rect(c, '#1b1d24', bx + 2, wy - 4.6, 2, 0.8);
  }
}

function flatStones(c: Ctx, r: Rect, t: number): void {
  const by = r.y + r.h, x = r.x;
  for (const [dx, dy, col] of [[4, -2, '#8a8884'], [10, -1, '#4a4a4e'], [16, -2.4, '#bdb8ae'], [22, -0.8, '#6a6864']] as const) {
    ellipse(c, '#2a2a2c', x + dx, by + dy + 0.3, 2.6, 1);
    ellipse(c, col, x + dx, by + dy, 2.3, 0.8);
  }
  // Skipping stones hopping out over the waves now and then.
  const cyc = (t * 0.35) % 1;
  if (cyc < 0.6) {
    const k = cyc / 0.6;
    for (let i = 0; i < 4; i++) {
      const hx = x + 34 + i * 16, hu = (k * 4 - i);
      if (hu > 0 && hu < 1.4) ellipse(c, `rgba(255,255,255,${0.7 * (1 - hu / 1.4)})`, hx, by - 6, 1.5 + hu * 4, 0.6 + hu * 1.2);
    }
    const sx = x + 26 + k * 64, sy = by - 6 - Math.abs(Math.sin(k * Math.PI * 4)) * 5;
    ellipse(c, '#4a4a4e', sx, sy, 1.4, 0.7);
  }
}

function dawnBridge(c: Ctx, r: Rect): void {
  // 曙光橋: a white arched walkway on the beach where people wait for the sunrise.
  const by = r.y + r.h, x = r.x, w = r.w, cx = x + w / 2;
  shadow(c, cx, by, w / 2 + 4, 3, 0.3, 20);
  const arch = (y0: number, rise: number, col: string, lw: number) => {
    c.strokeStyle = col;
    c.lineWidth = lw;
    c.beginPath();
    c.moveTo(x - 2, y0);
    c.quadraticCurveTo(cx, y0 - rise * 2, x + w + 2, y0);
    c.stroke();
  };
  for (const px of [x, x + w - 5]) block(c, px - 1, by - 8, 6, 8, '#e8e6e0', '#ffffff', '#b8b6ae');
  arch(by - 6, 12, '#a8a6a0', 5);
  arch(by - 7, 12, '#f8f8f4', 4);
  // Hangers and the handrail line.
  for (let i = 1; i < 8; i++) {
    const k = i / 8, hx = x - 2 + (w + 4) * k;
    const yDeck = by - 7 - 4 * k * (1 - k) * 24, yRail = yDeck - 8;
    rect(c, '#e8e6e0', hx - 0.4, yRail, 0.8, 8);
  }
  arch(by - 15, 12, '#d8d6d0', 1);
  // The sun motif on a plaque, and its glow at dawn.
  block(c, cx - 9, by - 13, 18, 6, '#1f6f9a', '#3a8fc0');
  txt(c, '曙光橋', cx, by - 9.9, 3.6, '#ffffff', 900);
  ellipse(c, '#ff9a3a', cx, by - 36, 3, 3);
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    rect(c, '#ffb84a', cx + Math.cos(a) * 5 - 0.4, by - 36 + Math.sin(a) * 5 - 0.4, 0.8, 0.8);
  }
  rect(c, '#e8e6e0', cx - 0.4, by - 31, 0.8, 7);
}

function starDeck(c: Ctx, r: Rect, t: number): void {
  const by = r.y + r.h, x = r.x, w = r.w, cx = x + w / 2;
  shadow(c, cx, by, w / 2 + 2, 3);
  // A low round deck with the Big Dipper inlaid in brass.
  ellipse(c, '#6a4428', cx, by - 4, w / 2 + 1, 6.5);
  ellipse(c, '#a8784a', cx, by - 5.5, w / 2, 5.8);
  for (let i = -3; i <= 3; i++) rect(c, 'rgba(90,60,30,0.3)', cx + i * 6, by - 10.6, 0.5, 10);
  const night = isNight();
  const dipper = [[-14, -6], [-9, -7], [-4, -6], [1, -5.4], [3, -3], [9, -3.4], [10, -6.4]];
  for (const [dx, dy] of dipper) {
    ellipse(c, night ? '#fff3b0' : '#d9b450', cx + dx, by + dy, 0.9, 0.6);
    if (night) ellipse(c, `rgba(255,240,170,${0.25 + 0.15 * Math.sin(t * 3 + dx)})`, cx + dx, by + dy, 2.6, 1.6);
  }
  line(c, night ? 'rgba(255,240,170,0.5)' : 'rgba(217,180,80,0.6)', 0.3, dipper.flatMap(([dx, dy]) => [cx + dx, by + dy]));
  // Telescope on a tripod.
  const tx = cx + 13, ty = by - 6;
  line(c, '#3a3e44', 0.7, [tx, ty - 10, tx - 3, ty, tx, ty - 10, tx + 3, ty, tx, ty - 10, tx + 0.4, ty + 1]);
  c.save();
  c.translate(tx, ty - 11);
  c.rotate(-0.5);
  block(c, -6, -1.6, 11, 3.2, '#f4f5f2', '#ffffff', '#c8ccd0');
  rect(c, '#1b2a36', 4.4, -1.4, 1.2, 2.8);
  c.restore();
  sign(c, '觀星', cx - 4, by - 28, 20, '#26365e');
}

function museumFront(c: Ctx, r: Rect, t: number): void {
  // Chihsing Tan Katsuo Museum: wooden doors, a name board and a giant bonito over the entrance.
  const cx = r.x + r.w / 2, wall = r.y + r.h - 16;
  block(c, cx - 11, wall - 20, 22, 20, '#6a4428', '#8a5a34', '#4a2e18');
  for (const dx of [-9.6, 0.6]) {
    block(c, cx + dx, wall - 18, 9, 18, '#a8784a', '#c8986a', '#7a5030');
    rect(c, '#e8d8b0', cx + dx + (dx < 0 ? 7 : 1), wall - 10, 1, 3);
  }
  block(c, cx - 26, wall - 31, 52, 9, '#1f4a6a', '#2f6a8a', '#123048');
  txt(c, '柴魚博物館', cx, wall - 26.4, 5, '#ffffff', 900, 'rgba(0,0,0,0.35)');
  // The bonito, smoked brown, gently “swimming”.
  const sw = Math.sin(t * 1.4) * 0.6;
  c.save();
  c.translate(cx, wall - 40 + sw);
  c.fillStyle = '#7a4a2a';
  c.beginPath();
  c.moveTo(-18, 0);
  c.quadraticCurveTo(-6, -7, 10, -3);
  c.lineTo(17, -7);
  c.lineTo(15, 0);
  c.lineTo(17, 6);
  c.lineTo(10, 3);
  c.quadraticCurveTo(-6, 6, -18, 0);
  c.fill();
  c.fillStyle = '#c8a070';
  c.beginPath();
  c.moveTo(-16, 1);
  c.quadraticCurveTo(-4, 5, 9, 2);
  c.quadraticCurveTo(-4, 2.2, -16, 1);
  c.fill();
  for (let i = -10; i < 8; i += 3) rect(c, 'rgba(40,20,10,0.4)', i, -4, 0.6, 3);
  ellipse(c, '#fff3d0', -13, -1, 1.4, 1.4);
  ellipse(c, '#1b1d24', -13, -1, 0.7, 0.7);
  c.restore();
  // Paper lanterns either side.
  for (const lx of [cx - 16, cx + 16]) {
    line(c, '#3a2418', 0.4, [lx, wall - 22, lx, wall - 19]);
    ellipse(c, '#e8473a', lx, wall - 16, 2.6, 3.4);
    rect(c, '#1b1d24', lx - 1.6, wall - 19.4, 3.2, 0.8);
    rect(c, '#1b1d24', lx - 1.6, wall - 13.2, 3.2, 0.8);
  }
}

const OBJECTS: Record<string, (c: Ctx, r: Rect, t: number) => void> = {
  tk_shuttleTaroko: (c, r) => shuttleStop(c, r, '太魯閣'),
  tk_shuttleBeach: (c, r) => shuttleStop(c, r, '七星潭'),
  tk_visitor: visitorDoors,
  tk_cimu: cimuPavilion,
  tk_rockSign: (c, r) => rockWarning(c, r.x + r.w / 2, r.y + r.h),
  tk_swallows: swallowBoard,
  tk_shrine: shrine,
  tk_bell: bellTower,
  tk_buluowan: buluowanSign,
  tk_weave: loom,
  tk_monkeys: monkeySign,
  tk_shakadang: shakadangBoard,
  tk_bikeRent: bikeRent,
  tk_skip: flatStones,
  tk_sunrise: dawnBridge,
  tk_stargaze: starDeck,
  tk_museum: museumFront,
  tk_mochi: (c, r, t) =>
    stall(c, r, t, '小米麻糬', '#c0392b', (c, r) => {
      // Plates of white and purple millet mochi, a bundle of golden millet.
      trukuBand(c, r.x, r.y - 12, r.w, 2.6);
      for (let k = 0; k < 3; k++) {
        const px = r.x + 8 + k * 11, py = r.y - 12;
        ellipse(c, '#f4f2ec', px, py, 4.6, 1.6);
        for (let i = 0; i < 3; i++) ellipse(c, k === 1 ? '#a87ac0' : '#fbf6ea', px - 2 + i * 2, py - 1.4 - (i % 2) * 0.8, 1.5, 1.2);
      }
      for (let i = 0; i < 5; i++) line(c, '#e0b040', 0.7, [r.x + r.w - 5, r.y - 30, r.x + r.w - 7 + i, r.y - 20]);
      rect(c, '#8a5a34', r.x + r.w - 6, r.y - 31, 3, 1.4);
    }),
  tk_sausage: (c, r, t) =>
    stall(c, r, t, '飛魚香腸', '#2f6fa8', (c, r, t) => {
      // A charcoal grill with sausages, smoke, and a flying-fish picture.
      const gx = r.x + 3, gy = r.y - 12;
      block(c, gx, gy - 2, r.w - 6, 4, '#2b2e33', '#4a4e54');
      for (let i = 0; i < 4; i++) {
        const sx = gx + 3 + i * 5.4;
        block(c, sx, gy - 4.4, 4.4, 2.4, '#b8442a', '#d8643a');
        rect(c, 'rgba(255,200,150,0.6)', sx + 0.6, gy - 4, 2, 0.4);
      }
      if (Math.sin(t * 9) > 0.3) ellipse(c, 'rgba(255,140,60,0.5)', gx + 10, gy - 0.6, 6, 1);
      steam(c, gx + 8, gy - 6, t, 3, 0.4);
      // Flying fish with spread fins.
      const fx = r.x + r.w / 2, fy = r.y - 34;
      ellipse(c, '#3a6a9a', fx, fy, 4, 1.4);
      poly(c, '#9ad0f0', [fx - 1, fy, fx + 2, fy - 4, fx + 3, fy]);
      poly(c, '#3a6a9a', [fx + 4, fy, fx + 6, fy - 1.6, fx + 6, fy + 1.6]);
    }),
};

// ---------------------------------------------------------------------------
// Decor
// ---------------------------------------------------------------------------

function cimuBridge(c: Ctx, d: Decor): void {
  // A red steel bridge running north over the river: two parapets with posts.
  const cx = d.x, w = d.w ?? 32, h = d.h ?? 80, by = d.y;
  for (const s of [-1, 1]) {
    const rx = cx + s * (w / 2 - 1.5) - 1.5;
    rect(c, 'rgba(0,0,0,0.25)', rx + 1, by - h + 2, 3, h);
    block(c, rx, by - h, 3, h, RED, RED_L, RED_D);
    for (let y = by - h + 6; y < by; y += 10) block(c, rx - 0.8, y - 4, 4.6, 5, RED_D, RED);
  }
}

function lion(c: Ctx, d: Decor): void {
  // A white marble guardian lion on a pedestal; variant 1 faces the other way.
  const x = d.x, y = d.y, s = d.variant ? -1 : 1;
  shadow(c, x, y, 6, 1.8);
  block(c, x - 5, y - 7, 10, 7, '#d8d4ca', '#ecebe4', '#a8a49a');
  ellipse(c, '#e8e6e0', x, y - 11, 4.4, 3.6);
  ellipse(c, '#f6f4ee', x + s * 1.4, y - 15.4, 3.6, 3.4);
  for (let i = 0; i < 5; i++) ellipse(c, '#d0ccc2', x + s * 1.4 + Math.cos(i * 1.3) * 3, y - 15.4 + Math.sin(i * 1.3) * 3, 1.2, 1.2);
  ellipse(c, '#f6f4ee', x + s * 2, y - 15, 2.2, 2);
  rect(c, '#55606a', x + s * 2.6 - 0.3, y - 16, 0.6, 0.6);
  ellipse(c, '#e8e6e0', x - s * 2, y - 8.4, 1.8, 1.2);
}

function fallNet(c: Ctx, d: Decor): void {
  const w = d.w ?? 64, L = d.x - w / 2, R = d.x + w / 2;
  if (d.variant) {
    // Draped over the slope (seen from above).
    const y0 = d.y + 1, y1 = d.y + 28;
    c.strokeStyle = 'rgba(70,74,80,0.55)';
    c.lineWidth = 0.4;
    c.beginPath();
    for (let x = L; x <= R; x += 4) {
      c.moveTo(x, y0);
      c.lineTo(x + 6, y1);
      c.moveTo(x + 6, y0);
      c.lineTo(x, y1);
    }
    c.stroke();
    for (const px of [L, R]) block(c, px - 1, y0 - 2, 2.4, 3, '#55606a');
    return;
  }
  // Hanging down the cliff face: anchor bar on top, a diamond mesh, cables.
  const top = d.y - 52, bot = d.y - 3;
  c.strokeStyle = 'rgba(60,64,70,0.5)';
  c.lineWidth = 0.4;
  c.beginPath();
  for (let x = L - 10; x <= R; x += 5) {
    c.moveTo(Math.max(L, x), top + Math.max(0, L - x) * 4.9);
    c.lineTo(Math.min(R, x + 10), top + Math.min(49, (Math.min(R, x + 10) - x) * 4.9));
    c.moveTo(Math.min(R, x + 10), top + Math.max(0, x + 10 - R) * 4.9);
    c.lineTo(Math.max(L, x), top + Math.min(49, (x + 10 - Math.max(L, x)) * 4.9));
  }
  c.stroke();
  rect(c, '#55606a', L, top - 1, w, 1.4);
  for (let x = L; x <= R; x += 16) {
    block(c, x - 1, top - 3, 2.6, 3, '#3a3e44');
    rect(c, 'rgba(60,64,70,0.6)', x, top, 0.5, bot - top);
  }
}

function swallowCliff(c: Ctx, d: Decor): void {
  // Hundreds of little holes in the marble where the swallows nest.
  const w = d.w ?? 128, L = d.x - w / 2, base = d.y;
  for (let i = 0; i < 52; i++) {
    const hx = L + 4 + h1(i, 11) * (w - 8);
    const hy = base - 8 - h1(i, 12) * h1(i, 13) * 62;
    const s = 0.7 + h1(i, 14) * 0.9;
    ellipse(c, 'rgba(255,255,255,0.45)', hx - 0.5, hy - 0.6, 2.2 * s, 1.5 * s);
    ellipse(c, '#3a3630', hx, hy, 2 * s, 1.3 * s);
    ellipse(c, '#1e1a16', hx + 0.3, hy + 0.2, 1.3 * s, 0.8 * s);
    if (i % 9 === 0) ellipse(c, '#4f8a3f', hx + 3, hy + 1.5, 2.4, 1);
  }
}

const h1 = (i: number, seed: number) => {
  const v = Math.sin(i * 127.1 + seed * 311.7) * 43758.5453;
  return v - Math.floor(v);
};

/** A south-facing marble canyon wall rising from the river (or a ledge), `h` px tall. */
function wall(c: Ctx, d: Decor): void {
  const w = d.w ?? 96, h = d.h ?? 56, L = d.x - w / 2, by = d.y, top = by - h;
  const bands = ['#e4e0d6', '#d6d2c8', '#c8c4ba', '#b8b4aa', '#a8a49a', '#948f86'];
  for (let k = 0; k < bands.length; k++) rect(c, bands[k], L, top + (k * h) / bands.length, w, h / bands.length + 0.5);
  // Vertical flutes and streaks, as on Taroko's marble walls.
  for (let i = 0; i < w; i += 3) {
    const v = h1(i + L, 1);
    const len = h * (0.35 + h1(i + L, 2) * 0.6);
    rect(c, v > 0.55 ? 'rgba(255,255,255,0.38)' : 'rgba(60,55,50,0.22)', L + i, top + v * 6, v > 0.8 ? 1.2 : 0.8, len);
  }
  // Tilted strata.
  c.strokeStyle = 'rgba(90,84,76,0.35)';
  c.lineWidth = 0.6;
  c.beginPath();
  for (let k = 1; k < 4; k++) {
    const y0 = top + (k * h) / 4 + h1(L, k) * 4;
    c.moveTo(L, y0);
    c.quadraticCurveTo(L + w / 2, y0 - 5 + h1(L, k + 5) * 10, L + w, y0 + 3);
  }
  c.stroke();
  // Overhang lip with plants, a dark wet foot.
  rect(c, '#f4f2ec', L, top, w, 1.2);
  rect(c, 'rgba(0,0,0,0.25)', L, top + 1.2, w, 1.4);
  for (let i = 4; i < w - 2; i += 9 + Math.floor(h1(i + L, 3) * 8)) {
    ellipse(c, '#3f7a35', L + i, top + 1, 3 + h1(i, 4) * 3, 1.8);
    ellipse(c, '#5f9a45', L + i - 1, top + 0.2, 2, 1);
    if (h1(i + L, 6) > 0.6) line(c, '#4f8a3f', 0.6, [L + i, top + 2, L + i + 0.6, top + 6 + h1(i, 7) * 6]);
  }
  rect(c, 'rgba(40,50,50,0.35)', L, by - 3, w, 3);
  rect(c, 'rgba(255,255,255,0.4)', L, by - 0.6, w, 0.6);
}

/** East-facing cliff where the Qingshui mountains drop straight into the Pacific. */
function eastCliff(c: Ctx, d: Decor, t: number): void {
  const h = d.h ?? 64, x = d.x, by = d.y, top = by - h;
  for (let k = 0; k < 4; k++) rect(c, ['#c8c4ba', '#a8a49a', '#8a867c', '#6e6a62'][k], x - 12 + k * 3, top, 3, h);
  for (let y = top + 3; y < by; y += 5) rect(c, 'rgba(255,255,255,0.3)', x - 12, y + h1(y, 1) * 2, 9 + h1(y, 2) * 3, 0.6);
  for (let y = top + 6; y < by; y += 14) ellipse(c, '#3f7a35', x - 11, y, 2.4, 3);
  for (let y = top; y < by; y += 8) {
    const sp = Math.max(0, Math.sin(t * 1.6 + y * 0.2));
    ellipse(c, `rgba(255,255,255,${0.55 * sp})`, x + 1, y + 4, 2 + sp * 2.5, 3);
  }
}

/** Ferns and shrubs clinging to the marble. */
function fern(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, v = d.variant ?? 0;
  if (v === 2) {
    ellipse(c, 'rgba(20,10,6,0.2)', x, y, 7, 2);
    ellipse(c, '#2f6a36', x, y - 4, 7, 4.5);
    ellipse(c, '#4a8a42', x - 2, y - 5.4, 4.4, 2.8);
    ellipse(c, '#6aa850', x - 3, y - 6.4, 2, 1.2);
    return;
  }
  for (let i = -3; i <= 3; i++) {
    const a = i * 0.38;
    line(c, i % 2 ? '#3f7a35' : '#5f9a45', 1.2, [x, y, x + Math.sin(a) * 8, y - Math.cos(a) * (6 + v * 2)]);
  }
}

function birds(c: Ctx, d: Decor, t: number): void {
  if (d.variant === 2) return gulls(c, d, t);
  // Swallows darting over the river and in and out of the cliff holes.
  const w = d.w ?? 128, L = d.x - w / 2;
  const n = 5;
  for (let i = 0; i < n; i++) {
    const p = i * 1.7 + (d.variant ?? 0) * 3.1;
    const a = t * (0.9 + i * 0.13) + p;
    const bx = L + w / 2 + Math.sin(a) * (w / 2 - 6) + Math.sin(a * 2.3) * 6;
    const by = d.y - 26 + Math.sin(a * 1.7 + p) * 22;
    const dir = Math.cos(a) >= 0 ? 1 : -1;
    const flap = Math.sin(t * 18 + i * 2) > 0;
    ellipse(c, 'rgba(0,0,0,0.12)', bx, d.y + 22 + (by - d.y) * 0.1, 2.2, 0.6);
    c.fillStyle = '#1b2a4a';
    c.beginPath();
    c.moveTo(bx - dir * 2.6, by);
    c.lineTo(bx - dir * 0.4, by + (flap ? -3.2 : 2));
    c.lineTo(bx + dir * 0.6, by);
    c.lineTo(bx + dir * 1.6, by + (flap ? -3.4 : 2.2));
    c.lineTo(bx + dir * 2.8, by + 0.2);
    c.lineTo(bx - dir * 0.4, by + 1);
    c.closePath();
    c.fill();
    rect(c, '#f4f1ea', bx - 0.6, by + 0.2, 1.4, 0.6);
    rect(c, '#c0392b', bx + dir * 2.2, by - 0.4, 0.6, 0.6);
    // Forked tail.
    line(c, '#1b2a4a', 0.4, [bx - dir * 2.6, by, bx - dir * 4.4, by - 0.8, bx - dir * 2.6, by, bx - dir * 4.2, by + 0.9]);
  }
}

/** Seagulls gliding in slow circles over the sea. */
function gulls(c: Ctx, d: Decor, t: number): void {
  for (let i = 0; i < 3; i++) {
    const a = t * (0.35 + i * 0.07) + i * 2.1;
    const gx = d.x + Math.cos(a) * (24 + i * 6), gy = d.y - 30 + Math.sin(a) * 10;
    const f = Math.sin(t * 5 + i) * 1.6;
    ellipse(c, 'rgba(10,40,70,0.18)', gx, d.y + 10, 2.4, 0.6);
    line(c, '#ffffff', 0.9, [gx - 4, gy - f, gx - 1.5, gy - 0.6, gx, gy, gx + 1.5, gy - 0.6, gx + 4, gy - f]);
    rect(c, '#55606a', gx - 0.5, gy - 0.3, 1, 0.8);
  }
}

function rail(c: Ctx, d: Decor): void {
  const w = d.w ?? 64, L = d.x - w / 2, y = d.y;
  const wood = d.variant === 1;
  const post = wood ? '#6a4a2a' : '#2f5a4a';
  const bar = wood ? '#8a6440' : '#3f7a62';
  rect(c, 'rgba(0,0,0,0.18)', L, y + 0.5, w, 1);
  rect(c, bar, L, y - 9, w, 1.4);
  rect(c, shade(bar, 0.3), L, y - 9, w, 0.5);
  rect(c, bar, L, y - 4.6, w, 0.9);
  for (let x = L + 1; x < L + w; x += 12) block(c, x - 0.8, y - 10, 2.2, 10, post);
}

function redRail(c: Ctx, d: Decor): void {
  const w = d.w ?? 48, L = d.x - w / 2, y = d.y;
  rect(c, 'rgba(0,0,0,0.2)', L, y + 0.5, w, 1);
  block(c, L, y - 9, w, 2.4, RED, RED_L, RED_D);
  rect(c, RED, L, y - 4.6, w, 1);
  for (let x = L; x <= L + w - 2; x += 8) block(c, x, y - 10, 2.4, 10, RED_D, RED);
  for (const px of [L - 2, L + w - 2]) block(c, px, y - 13, 4.4, 13, '#f2efe6', '#ffffff', '#c8c4b8');
  txt(c, '砂', L, y - 8, 2.4, RED, 900);
}

function bridgeRail(c: Ctx, d: Decor): void {
  // Concrete parapet of the coastal highway bridge, running north over the river mouth.
  const h = d.h ?? 80, x = d.x, by = d.y;
  rect(c, 'rgba(0,0,0,0.25)', x + 1, by - h + 2, 3, h);
  block(c, x, by - h, 3, h, '#d8d4ca', '#ecebe4', '#a8a49a');
  for (let y = by - h + 4; y < by; y += 12) rect(c, '#a8a49a', x, y, 3, 0.8);
}

function lantern(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y;
  shadow(c, x, y, 3, 1.2);
  block(c, x - 2, y - 6, 4, 6, '#b8b4aa', '#d8d4ca');
  rect(c, '#5a3a20', x - 0.6, y - 16, 1.2, 10);
  ellipse(c, '#e8473a', x, y - 18, 3, 3.6);
  rect(c, '#1b1d24', x - 1.8, y - 21.6, 3.6, 0.9);
  rect(c, '#1b1d24', x - 1.8, y - 15.2, 3.6, 0.9);
  rect(c, 'rgba(255,220,150,0.6)', x - 0.6, y - 20, 1, 3.6);
  txt(c, '福', x, y - 18, 2.8, '#ffd34e', 900);
}

function trukuHouse(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, v = d.variant ?? 0;
  shadow(c, x, y, 44, 3.4, 0.3, 30);
  if (v === 2) {
    // A granary on stilts with rat-guard discs, thatched roof.
    for (const px of [x - 20, x - 7, x + 6, x + 19]) {
      block(c, px - 1.4, y - 14, 3, 14, '#6a4a2a', '#8a6440');
      ellipse(c, '#5a3a20', px, y - 14, 4, 1.2);
    }
    block(c, x - 24, y - 34, 48, 20, '#8a6440', '#a8845a', '#5a3a20');
    for (let i = x - 22; i < x + 24; i += 4) rect(c, 'rgba(60,30,10,0.35)', i, y - 33, 0.6, 18);
    rect(c, '#5a3a20', x - 4, y - 28, 8, 12);
    trukuBand(c, x - 24, y - 20, 48, 2.4);
  } else {
    // Walls: wood planks (0) or stacked slate slabs (1).
    if (v === 1) {
      block(c, x - 40, y - 26, 80, 26, '#7a7a74', '#9a9a92', '#55554e');
      for (let row = 0; row < 6; row++) {
        for (let i = 0; i < 10; i++) {
          const sx = x - 39 + i * 8 + (row % 2) * 4, sy = y - 25 + row * 4.2;
          rect(c, ['#8a8a82', '#6a6a64', '#9a9890'][(i + row) % 3], Math.min(sx, x + 32), sy, 7, 3.4);
        }
      }
    } else {
      block(c, x - 40, y - 26, 80, 26, '#8a5a34', '#a8784a', '#5a3a20');
      for (let i = x - 38; i < x + 40; i += 5) rect(c, 'rgba(50,25,10,0.35)', i, y - 25, 0.7, 24);
      // Bamboo poles across.
      for (const by of [y - 22, y - 8]) rect(c, '#c8b060', x - 40, by, 80, 1.4);
    }
    // Low doorway with a woven cloth hanging, a small window.
    rect(c, '#2a1810', x - 6, y - 18, 12, 18);
    trukuBand(c, x - 6, y - 18, 12, 3);
    rect(c, '#2a1810', x + 18, y - 18, 9, 6);
    for (let i = 0; i < 3; i++) rect(c, '#8a6440', x + 19 + i * 3, y - 18, 0.8, 6);
  }
  // Thatched roof.
  const top = v === 2 ? y - 54 : y - 46, eave = v === 2 ? y - 32 : y - 24;
  const half = v === 2 ? 30 : 46;
  poly(c, '#7a6230', [x - half - 3, eave + 2, x + half + 3, eave + 2, x + half * 0.55, top, x - half * 0.55, top]);
  poly(c, '#b8984a', [x - half - 1, eave, x + half + 1, eave, x + half * 0.55, top + 1.5, x - half * 0.55, top + 1.5]);
  for (let i = -half; i < half; i += 2.6) {
    const k = Math.abs(i) / half;
    line(c, 'rgba(110,80,30,0.55)', 0.5, [x + i * 0.55, top + 2, x + i, eave - 0.4 + k * 0.5]);
  }
  rect(c, 'rgba(255,240,180,0.3)', x - half * 0.5, top + 2, half, 0.6);
  // Ridge tied with rope.
  block(c, x - half * 0.58, top - 2.6, half * 1.16, 3.2, '#6a5228', '#8a6a38');
  for (let i = -half * 0.5; i < half * 0.55; i += 8) rect(c, '#3a2a14', x + i, top - 2.6, 1, 3.4);
}

function milletRack(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y;
  shadow(c, x, y, 16, 2.4);
  for (const px of [x - 14, x + 12]) block(c, px, y - 22, 2.6, 22, '#6a4a2a', '#8a6440');
  rect(c, '#8a6440', x - 16, y - 21, 32, 1.6);
  for (let i = 0; i < 6; i++) {
    const bx = x - 11 + i * 4.4;
    rect(c, '#8a6a20', bx - 0.3, y - 20, 0.6, 2);
    ellipse(c, '#e0b040', bx, y - 15, 1.8, 3.8);
    ellipse(c, '#f0d070', bx - 0.5, y - 16, 0.8, 2.2);
  }
  if (d.variant) {
    // A wooden mortar and pestle for pounding millet.
    block(c, x + 15, y - 9, 8, 9, '#8a5a34', '#a8784a', '#5a3a20');
    ellipse(c, '#3a2418', x + 19, y - 9, 3, 1);
    line(c, '#a8784a', 1.6, [x + 19, y - 10, x + 21, y - 24]);
  }
}

function firePit(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y;
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    ellipse(c, '#6a6a64', x + Math.cos(a) * 7, y - 3 + Math.sin(a) * 2.6, 2.2, 1.4);
  }
  line(c, '#4a2a14', 1.6, [x - 4, y - 2, x + 4, y - 4]);
  line(c, '#5a3418', 1.6, [x - 4, y - 4, x + 4, y - 2]);
  for (let i = 0; i < 3; i++) {
    const f = Math.sin(t * 9 + i * 2) * 1.2;
    poly(c, i === 1 ? '#ffd34e' : '#ff7a2a', [x - 3 + i * 3, y - 3, x - 1 + i * 2 + f * 0.4, y - 9 - f - (i === 1 ? 3 : 0), x + 1 + i * 1.5, y - 3]);
  }
  ellipse(c, 'rgba(255,160,60,0.25)', x, y - 5, 9, 5);
  steam(c, x, y - 12, t, 3, 0.25);
  for (const [dx, dy] of [[-13, 2], [13, 1]]) block(c, x + dx - 3, y + dy - 4, 6, 4, '#8a6440', '#a8845a');
}

function monkey(c: Ctx, d: Decor, t: number): void {
  // Formosan rock monkey: grey-brown fur, a pinkish face, a long tail.
  const x = d.x, y = d.y, v = d.variant ?? 0;
  const s = v === 1 ? -1 : 1;
  ellipse(c, 'rgba(20,10,6,0.25)', x, y, 5, 1.4);
  const sway = Math.sin(t * 1.3 + x) * 1.2;
  line(c, '#7a6a52', 1.2, [x - s * 3, y - 2, x - s * 7, y - 1, x - s * 9 + sway, y - 5]);
  ellipse(c, '#8a7a5e', x, y - 4.6, 4, 4);
  ellipse(c, '#a8987a', x + s * 0.6, y - 4, 2.4, 2.8);
  const look = Math.sin(t * 0.7 + x * 0.3) > 0.6 ? s * 0.8 : 0;
  ellipse(c, '#8a7a5e', x + s * 1 + look, y - 10, 3.2, 3);
  ellipse(c, '#e8a890', x + s * 1.6 + look, y - 9.6, 2, 1.8);
  rect(c, '#1b1d24', x + s * 1 + look, y - 10.4, 0.6, 0.6);
  rect(c, '#1b1d24', x + s * 2.3 + look, y - 10.4, 0.6, 0.6);
  // Arm: scratching (0), eating (2) or resting.
  if (v === 0) {
    const k = Math.sin(t * 6) > 0 ? 1 : 0;
    line(c, '#7a6a52', 1, [x - s * 2, y - 6, x - s * 3, y - 9 - k]);
  } else if (v === 2) {
    const k = Math.max(0, Math.sin(t * 2.4));
    line(c, '#7a6a52', 1, [x + s * 2.4, y - 5, x + s * 2.6, y - 7 - k * 2]);
    ellipse(c, '#e0b040', x + s * 2.8, y - 8 - k * 2, 1, 0.8);
  }
}

function tourBus(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, v = d.variant ?? 0;
  const L = x - 36, W = 72, top = y - 26;
  shadow(c, x, y, 38, 3.4, 0.3);
  const body = v === 2 ? '#f4f5f2' : '#fbfbf8';
  block(c, L, top, W, 22, body, '#ffffff', '#c8ccd0');
  // Livery.
  const a = v === 2 ? '#2f9a5a' : v === 1 ? '#2f6fa8' : '#e2493b';
  const b2 = v === 2 ? '#ffd34e' : v === 1 ? '#3ad9c0' : '#ffd34e';
  poly(c, a, [L + 1, top + 14, L + W - 1, top + 9, L + W - 1, top + 13, L + 1, top + 18]);
  poly(c, b2, [L + 1, top + 18, L + W - 1, top + 13, L + W - 1, top + 14.6, L + 1, top + 19.6]);
  // Windows.
  rect(c, '#2a3a4a', L + 3, top + 3, W - 14, 7);
  for (let i = L + 12; i < L + W - 11; i += 9) rect(c, body, i, top + 3, 0.8, 7);
  rect(c, 'rgba(180,220,255,0.3)', L + 4, top + 4, W - 16, 1.2);
  // Front (right end) with a destination board.
  block(c, L + W - 10, top + 2, 9, 12, '#2a3a4a', '#4a5a6a');
  rect(c, '#ffb020', L + W - 9, top + 2.6, 7, 2);
  if (v === 2) txt(c, '台灣好行', L + 26, top + 15.6, 4.2, '#ffffff', 900, 'rgba(0,0,0,0.3)');
  else txt(c, v ? '觀光巴士' : 'TOUR', L + 26, top + 15.6, 4, '#ffffff', 900, 'rgba(0,0,0,0.3)');
  rect(c, '#ffd34e', L + W - 2, top + 17, 1.6, 2);
  // Wheels.
  for (const wx of [L + 13, L + W - 16]) {
    ellipse(c, '#1b1d24', wx, y - 3, 4.6, 4.2);
    ellipse(c, '#8a9096', wx, y - 3, 2, 1.8);
  }
}

function bench(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y;
  shadow(c, x, y, 9, 2);
  for (const lx of [x - 7, x + 5]) block(c, lx, y - 5, 2, 5, '#3a3e44');
  block(c, x - 9, y - 8, 18, 3, '#a8784a', '#c8986a');
  block(c, x - 9, y - 14, 18, 3, '#a8784a', '#c8986a');
  rect(c, '#3a3e44', x - 8, y - 11, 1, 3);
  rect(c, '#3a3e44', x + 7, y - 11, 1, 3);
}

function surf(c: Ctx, d: Decor, t: number): void {
  // Waves running up the pebbles (or crashing into the cliff foot) along the shore.
  const rows = Math.round((d.h ?? 64) / T);
  const ty1 = Math.round(d.y / T);
  for (let k = 0; k < rows; k++) {
    const ty = ty1 - rows + k;
    const sx = shoreX(ty) * T, y0 = ty * T;
    const cliff = ty < 13;
    for (let j = 0; j < 2; j++) {
      const u = (t * 0.22 + ty * 0.07 + j * 0.5) % 1;
      const reach = cliff ? 3 : 7;
      const wx = sx + 26 * (1 - u) - reach * Math.sin(u * Math.PI);
      const a = Math.sin(u * Math.PI) * 0.8;
      c.fillStyle = `rgba(255,255,255,${a})`;
      c.beginPath();
      c.moveTo(wx, y0);
      c.quadraticCurveTo(wx - 3, y0 + 8, wx, y0 + 16);
      c.lineTo(wx + 2, y0 + 16);
      c.quadraticCurveTo(wx - 1, y0 + 8, wx + 2, y0);
      c.fill();
    }
    if (cliff) {
      const sp = Math.max(0, Math.sin(t * 1.4 + ty));
      ellipse(c, `rgba(255,255,255,${0.5 * sp})`, sx + 2, y0 + 8, 3 + sp * 3, 4);
    } else {
      // Wet pebbles darken where the wave just was.
      const u = (t * 0.22 + ty * 0.07) % 1;
      rect(c, `rgba(40,60,80,${0.18 * (1 - u)})`, sx - 6, y0, 6, 16);
    }
  }
}

function boatBeached(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y;
  shadow(c, x, y, 26, 3);
  for (const rx of [x - 10, x + 8]) block(c, rx - 4, y - 2, 8, 2.4, '#6a4a2a');
  boat(c, x, y - 2, 1, d.variant ? '#f4f5f2' : '#2f6fa8', d.variant ? '#2f9a5a' : '#ffffff', t, false);
}

function boatSea(c: Ctx, d: Decor, t: number): void {
  const bob = Math.sin(t * 1.3) * 1.2;
  ellipse(c, 'rgba(10,40,70,0.35)', d.x + 2, d.y + 1, 24, 3);
  c.save();
  c.translate(d.x, d.y + bob);
  c.rotate(Math.sin(t * 1.1) * 0.04);
  boat(c, 0, 0, 0.9, '#e8473a', '#ffffff', t);
  c.restore();
  const k = (t * 0.6) % 1;
  ellipse(c, `rgba(255,255,255,${0.5 * (1 - k)})`, d.x - 22 - k * 8, d.y + 1, 3 + k * 5, 1);
}

function qixingStone(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y;
  shadow(c, x, y, 18, 3);
  c.fillStyle = '#4a5a64';
  c.beginPath();
  c.moveTo(x - 16, y);
  c.quadraticCurveTo(x - 18, y - 16, x - 6, y - 22);
  c.quadraticCurveTo(x + 8, y - 26, x + 16, y - 14);
  c.quadraticCurveTo(x + 18, y - 4, x + 15, y);
  c.fill();
  c.fillStyle = '#6a7a84';
  c.beginPath();
  c.moveTo(x - 14, y - 2);
  c.quadraticCurveTo(x - 15, y - 15, x - 5, y - 20);
  c.quadraticCurveTo(x + 7, y - 23, x + 13, y - 13);
  c.quadraticCurveTo(x + 14, y - 5, x + 12, y - 2);
  c.fill();
  ellipse(c, 'rgba(255,255,255,0.25)', x - 6, y - 17, 5, 1.6);
  txt(c, '七星潭', x, y - 11, 6, '#ffffff', 900, 'rgba(0,0,0,0.4)');
  for (let i = 0; i < 7; i++) ellipse(c, '#ffd34e', x - 9 + i * 3, y - 5 - Math.abs(i - 3) * 0.5, 0.6, 0.6);
}

function cyclists(c: Ctx, d: Decor, t: number): void {
  // Riders on the cycle path; each segment only draws a rider while one is passing through it.
  const segBot = d.y, segTop = d.y - (d.h ?? 64);
  const Y0 = 20 * T, Y1 = 44 * T, period = Y1 - Y0 + 160;
  const riders = [
    { speed: 26, off: 0, lane: 78 * T + 8, south: true, top: '#3ad9c0' },
    { speed: 20, off: 230, lane: 79 * T + 8, south: false, top: '#ff8ac0' },
    { speed: 30, off: 120, lane: 78 * T + 8, south: true, top: '#ffd34e' },
  ];
  for (const r of riders) {
    const p = (t * r.speed + r.off) % period;
    const yy = r.south ? Y0 + p : Y1 - p;
    if (yy < segTop || yy >= segBot) continue;
    const x = r.lane, y = yy;
    const pedal = Math.sin(t * 8 + r.off);
    ellipse(c, 'rgba(20,10,6,0.25)', x, y + 0.5, 3, 1);
    rect(c, '#1b1d24', x - 0.5, y - 9, 1, 9);
    rect(c, '#55606a', x - 3, y - 9, 6, 0.8);
    rect(c, '#2f3442', x - 2 + pedal, y - 6, 1.2, 3);
    rect(c, '#2f3442', x + 0.8 - pedal, y - 6, 1.2, 3);
    block(c, x - 2.6, y - 13, 5.2, 5.4, r.top, shade(r.top, 0.2));
    ellipse(c, '#f0c8a4', x, y - 15, 2, 2);
    c.fillStyle = r.south ? '#e2493b' : '#3a8fd9';
    c.beginPath();
    c.ellipse(x, y - 15.6, 2.3, 1.8, 0, Math.PI, 0);
    c.fill();
  }
}

function palm(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y;
  shadow(c, x + 4, y, 10, 2.4, 0.25, 40);
  const sw = wind.at(x, y) * 1.5 + Math.sin(t * 0.8 + x) * 0.6;
  const lean = d.variant ? 4 : -3;
  line(c, '#6a5232', 3, [x, y, x + lean * 0.5, y - 18, x + lean + sw * 0.5, y - 36]);
  for (let i = 0; i < 6; i++) rect(c, '#8a7048', x + lean * (i / 6) * 0.9 - 1.5, y - 4 - i * 5.6, 3, 0.8);
  const hx = x + lean + sw * 0.5, hy = y - 37;
  const fronds = [[-14, 3], [-10, -6], [-3, -9], [5, -8], [12, -4], [15, 4], [-7, 6], [8, 6]];
  for (const [fx, fy] of fronds) {
    const ex = hx + fx + sw, ey = hy + fy + 2;
    c.strokeStyle = '#2f7a3a';
    c.lineWidth = 2.2;
    c.beginPath();
    c.moveTo(hx, hy);
    c.quadraticCurveTo(hx + fx * 0.5, hy + fy * 0.5 - 4, ex, ey);
    c.stroke();
    c.strokeStyle = '#5aa04a';
    c.lineWidth = 0.8;
    c.stroke();
  }
  ellipse(c, '#7a5a2a', hx - 1, hy + 1, 1.6, 1.4);
  ellipse(c, '#7a5a2a', hx + 1.4, hy + 1.6, 1.6, 1.4);
}

const SIGNS: [string[], string][] = [
  [['海', '景', '民', '宿'], '#2f6fa8'],
  [['海', '產', '小', '吃'], RED],
];

function shopSign(c: Ctx, d: Decor): void {
  const [chars, col] = SIGNS[(d.variant ?? 0) % SIGNS.length];
  const x = d.x, top = d.y - 46, by = d.y - 8;
  rect(c, '#3a3e44', x - 5, top + 4, 10, 1);
  block(c, x - 4.5, top, 9, by - top, col, shade(col, 0.25), shade(col, -0.3));
  rect(c, '#ffd34e', x - 3.5, top + 1.5, 7, 0.5);
  chars.forEach((ch, i) => txt(c, ch, x, top + 5.5 + i * ((by - top - 6) / chars.length), 5.6, '#fff3b0', 900, 'rgba(0,0,0,0.4)'));
}

/** Brown park direction sign at the junction. */
function dirSign(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y;
  shadow(c, x, y, 5, 1.4);
  block(c, x - 1.2, y - 30, 2.6, 30, '#5a3a20', '#7a5030');
  poly(c, '#7a5034', [x - 18, y - 30, x + 12, y - 30, x + 12, y - 23, x - 18, y - 23, x - 21, y - 26.5]);
  txt(c, '← 太魯閣 Taroko', x - 4, y - 26.5, 3.4, '#fff3d0', 900);
  poly(c, '#2f6fa8', [x - 12, y - 21, x + 18, y - 21, x + 21, y - 17.5, x + 18, y - 14, x - 12, y - 14]);
  txt(c, '七星潭 Qixingtan ↓', x + 4, y - 17.5, 3.2, '#ffffff', 900);
}

function vcSign(c: Ctx, d: Decor): void {
  // The name board along the top of the visitor centre facade.
  const x = d.x, y = d.y - 34;
  block(c, x - 44, y - 6, 88, 11, '#f2efe6', '#ffffff', '#c8c4b8');
  rect(c, '#2f6a4a', x - 44, y + 3.4, 88, 1.6);
  txt(c, '太魯閣國家公園遊客中心', x, y - 1.2, 5.2, '#2f5a3a', 900);
  txt(c, 'TAROKO NATIONAL PARK VISITOR CENTER', x, y + 2.4, 2, '#55606a', 700);
}

function parkStone(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y;
  shadow(c, x, y, 20, 3.2, 0.3, 20);
  block(c, x - 18, y - 4, 36, 4, '#8a867c', '#a8a49a');
  c.fillStyle = '#c8c4ba';
  c.beginPath();
  c.moveTo(x - 17, y - 3);
  c.quadraticCurveTo(x - 19, y - 20, x - 8, y - 26);
  c.quadraticCurveTo(x + 10, y - 30, x + 17, y - 18);
  c.quadraticCurveTo(x + 19, y - 8, x + 16, y - 3);
  c.fill();
  c.fillStyle = '#ecebe4';
  c.beginPath();
  c.moveTo(x - 15, y - 5);
  c.quadraticCurveTo(x - 16, y - 19, x - 7, y - 24);
  c.quadraticCurveTo(x + 9, y - 27, x + 14, y - 17);
  c.quadraticCurveTo(x + 16, y - 9, x + 14, y - 5);
  c.fill();
  line(c, 'rgba(120,120,128,0.45)', 0.5, [x - 12, y - 8, x - 4, y - 18, x + 6, y - 14, x + 12, y - 22]);
  txt(c, '太魯閣', x, y - 17, 6, RED, 900);
  txt(c, '國家公園', x, y - 10.6, 4.2, RED, 900);
}

// ---------------------------------------------------------------------------
// Art pass: tall marble walls, mist, rapids, hikers, butterflies, beach life
// ---------------------------------------------------------------------------

const BITMAPS = new Map<string, { cv: HTMLCanvasElement; s: number }>();

/** Paint once into an offscreen bitmap (per zoom step) and blit; `x0,y0,w,h` is the world rect. */
function cachedArt(c: Ctx, key: string, x0: number, y0: number, w: number, h: number, draw: (cc: Ctx) => void): void {
  const m = c.getTransform();
  const s = Math.min(3, Math.max(1, Math.round(Math.hypot(m.a, m.b) * 2) / 2));
  let e = BITMAPS.get(key);
  if (!e || e.s !== s) {
    const cv = document.createElement('canvas');
    cv.width = Math.ceil(w * s);
    cv.height = Math.ceil(h * s);
    const cc = cv.getContext('2d')!;
    cc.scale(s, s);
    cc.translate(-x0, -y0);
    draw(cc);
    e = { cv, s };
    BITMAPS.set(key, e);
  }
  c.drawImage(e.cv, x0, y0, w, h);
}

/**
 * A tall marble canyon wall: folded white-and-grey veins, dark wet streaks, a lip of hanging ferns
 * and creepers, and a shaded wet foot where it meets the river. Cached per size/variant.
 */
function marbleWall(c: Ctx, d: Decor): void {
  const w = d.w ?? 96, h = d.h ?? 110, L = d.x - w / 2, by = d.y, top = by - h, v = d.variant ?? 0;
  cachedArt(c, `tkMarble:${w}x${h}:${v}:${Math.round(L)}`, L - 4, top - 8, w + 8, h + 12, (c) => {
    const bands = ['#ece9e2', '#dcd8d0', '#cfcbc3', '#c0bcb4', '#aeaaa2', '#9a968e', '#86827a'];
    for (let k = 0; k < bands.length; k++) rect(c, bands[k], L, top + (k * h) / bands.length, w, h / bands.length + 0.6);
    // Folded strata: wavy bands of white and dark grey marble running across the face.
    for (let k = 0; k < 7; k++) {
      const y0 = top + 8 + (k * (h - 16)) / 7 + h1(L + v, k) * 6;
      const amp = 4 + h1(L, k + 20) * 8;
      const white = k % 2 === 0;
      c.strokeStyle = white ? 'rgba(255,255,255,0.55)' : 'rgba(70,64,58,0.45)';
      c.lineWidth = white ? 1.6 + h1(k, v) * 1.6 : 0.9;
      c.beginPath();
      c.moveTo(L, y0);
      for (let x = L; x <= L + w; x += 8) c.lineTo(x, y0 + Math.sin((x - L) / 14 + k + v) * amp + Math.sin((x - L) / 5 + k * 2) * 1.2);
      c.stroke();
    }
    // Vertical flutes and water streaks.
    for (let i = 0; i < w; i += 2.4) {
      const r = h1(i + L, 1 + v);
      const len = h * (0.25 + h1(i + L, 2) * 0.7);
      rect(c, r > 0.62 ? 'rgba(255,255,255,0.3)' : r < 0.2 ? 'rgba(50,46,42,0.3)' : 'rgba(90,84,76,0.12)', L + i, top + 3 + r * 6, r > 0.85 ? 1.4 : 0.8, len);
    }
    for (let i = 0; i < w; i += 11 + Math.floor(h1(i + L, 31) * 9)) {
      if (h1(i + L, 32) < 0.45) continue;
      const sx = L + i + 2, len = h * (0.5 + h1(i, 33) * 0.5);
      rect(c, 'rgba(40,60,70,0.35)', sx, by - len, 1.6, len);
      rect(c, 'rgba(180,220,230,0.35)', sx + 0.4, by - len, 0.5, len);
    }
    // Cracks and the odd dark cavity.
    c.strokeStyle = 'rgba(40,36,32,0.5)';
    c.lineWidth = 0.6;
    c.beginPath();
    for (let k = 0; k < 5; k++) {
      const x0 = L + h1(k, v + 40) * w, y0 = top + 10 + h1(k, v + 41) * (h - 30);
      c.moveTo(x0, y0);
      c.lineTo(x0 + 3, y0 + 9);
      c.lineTo(x0 - 1, y0 + 17);
      c.lineTo(x0 + 2, y0 + 26);
    }
    c.stroke();
    for (let k = 0; k < 3; k++) {
      const x0 = L + 8 + h1(k, v + 50) * (w - 16), y0 = top + 14 + h1(k, v + 51) * (h - 40);
      ellipse(c, 'rgba(30,28,26,0.75)', x0, y0, 3 + h1(k, 52) * 3, 2 + h1(k, 53) * 2);
    }
    // Lip with hanging vegetation and creepers.
    rect(c, '#f6f4ee', L, top, w, 1.4);
    rect(c, 'rgba(0,0,0,0.3)', L, top + 1.4, w, 1.6);
    for (let i = 2; i < w - 2; i += 7 + Math.floor(h1(i + L, 3) * 7)) {
      const gx = L + i, gy = top + 1;
      ellipse(c, '#2f6a30', gx, gy, 3.5 + h1(i, 4) * 3, 2.2);
      ellipse(c, '#4f8a3f', gx - 1, gy - 0.6, 2.4 + h1(i, 5) * 2, 1.4);
      ellipse(c, '#7ab050', gx - 1.6, gy - 1.2, 1.2, 0.7);
      const n = 1 + Math.floor(h1(i + L, 6) * 3);
      for (let k = 0; k < n; k++) {
        const len = 8 + h1(i + k, 7) * (h * 0.35);
        const vx = gx + (k - 1) * 2.2;
        line(c, k % 2 ? '#3f7a35' : '#5a9a45', 0.7, [vx, gy + 2, vx + 0.8, gy + len * 0.5, vx - 0.6, gy + len]);
        for (let j = 4; j < len; j += 5) ellipse(c, '#4f8a3f', vx + (j % 2 ? 1.2 : -1.2), gy + j, 1.2, 0.8);
      }
    }
    // Mid-wall ledge with ferns, on the longer walls.
    if (w >= 80) {
      const ly = top + h * 0.55 + h1(L, 60) * 10;
      rect(c, 'rgba(255,255,255,0.4)', L + w * 0.2, ly, w * 0.5, 1);
      rect(c, 'rgba(0,0,0,0.25)', L + w * 0.2, ly + 1, w * 0.5, 1.4);
      for (let i = L + w * 0.24; i < L + w * 0.66; i += 9) {
        ellipse(c, '#3f7a35', i, ly, 3.4, 1.8);
        ellipse(c, '#6aa850', i - 1, ly - 0.8, 1.8, 1);
      }
    }
    // Wet dark foot and the splash line at the water.
    rect(c, 'rgba(30,45,50,0.4)', L, by - 5, w, 5);
    rect(c, 'rgba(255,255,255,0.45)', L, by - 0.8, w, 0.8);
  });
}

/** A slow bank of river mist drifting through the gorge (translucent, animated). */
function mist(c: Ctx, d: Decor, t: number): void {
  const w = d.w ?? 160, v = d.variant ?? 0, a0 = 0.22 + (v % 2) * 0.08;
  if (isNight() && v > 2) return;
  for (let i = 0; i < 6; i++) {
    const k = (t * 0.018 + i / 6 + v * 0.11) % 1;
    const x = d.x - w / 2 + k * w;
    const y = d.y - 6 + Math.sin(t * 0.3 + i * 1.3 + v) * 3;
    const a = a0 * Math.sin(k * Math.PI);
    ellipse(c, `rgba(236,244,246,${a})`, x, y, 26 + (i % 3) * 8, 6 + (i % 2) * 2);
    ellipse(c, `rgba(255,255,255,${a * 0.6})`, x - 8, y - 2, 14, 3.5);
  }
}

/** White water and glints where the river runs over rocks (anchored on a boulder). */
function rapids(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y, w = d.w ?? 40;
  for (let i = 0; i < 5; i++) {
    const k = (t * 0.5 + i / 5 + h1(i, 1)) % 1;
    const sx = x - w / 2 + k * w, sy = y - 3 + (h1(i, 2) - 0.5) * 8;
    const a = 0.75 * Math.sin(k * Math.PI);
    ellipse(c, `rgba(255,255,255,${a})`, sx, sy, 5 + h1(i, 3) * 4, 1 + h1(i, 4) * 0.6);
  }
  // Foam ring around the rock and sparkles.
  ellipse(c, 'rgba(255,255,255,0.35)', x, y + 1, w * 0.3, 2.4);
  for (let i = 0; i < 4; i++) {
    const sp = Math.max(0, Math.sin(t * 3 + i * 1.9 + x));
    if (sp > 0.8) rect(c, '#ffffff', x - w / 2 + h1(i, 5) * w, y - 10 + h1(i, 6) * 16, 1.2, 1.2);
  }
}

/** Sparkling glints on the turquoise river (a wide, flat, animated layer). */
function glints(c: Ctx, d: Decor, t: number): void {
  const w = d.w ?? 160, h = d.h ?? 48, L = d.x - w / 2, top = d.y - h;
  const night = isNight();
  for (let i = 0; i < 14; i++) {
    const sx = L + h1(i, 11) * w + ((t * (6 + h1(i, 12) * 8)) % 30) - 15;
    const sy = top + h1(i, 13) * h;
    const sp = Math.sin(t * 2.2 + i * 1.3) * 0.5 + 0.5;
    if (sp < 0.45) continue;
    const a = (sp - 0.45) * (night ? 0.9 : 1.6);
    rect(c, `rgba(255,255,255,${Math.min(1, a)})`, sx, sy, 3 + sp * 3, 0.8);
  }
  // Slow current lines.
  for (let i = 0; i < 4; i++) {
    const sy = top + 6 + i * (h / 4) + Math.sin(t * 0.4 + i) * 1.5;
    const k = ((t * 9 + i * 40) % (w + 60)) - 30;
    ellipse(c, 'rgba(230,250,250,0.35)', L + k, sy, 16, 0.9);
  }
}

const HIKER_LOOKS: CharLook[] = [
  { skin: '#f0c8a4', hair: '#2a1d16', hairStyle: 'short', top: '#e2493b', topShade: '#b83a2e', bottom: '#5b4636', shoes: '#2b1d14' },
  { skin: '#e8b48c', hair: '#1c1814', hairStyle: 'pony', top: '#3a8fd9', topShade: '#2a6fb0', bottom: '#1b1d24', shoes: '#f4f1ea' },
  { skin: '#f8d6b8', hair: '#6b3f22', hairStyle: 'messy', top: '#ffd34e', topShade: '#d9a82a', bottom: '#2f3442', shoes: '#e2493b', small: true },
  { skin: '#d9a47a', hair: '#9a948a', hairStyle: 'neat', top: '#57c278', topShade: '#3a9a58', bottom: '#5b4636', shoes: '#2b1d14', glasses: true },
  { skin: '#f3cba6', hair: '#3a2418', hairStyle: 'bun', top: '#f4f1ea', topShade: '#cfc8bb', bottom: '#4a6a8a', shoes: '#1d1814' },
];
const HELMETS = ['#ffffff', '#ffd34e', '#ffffff', '#e2493b', '#ffffff'];

/** Hikers in hard hats (the park hands them out), with a backpack, standing along the trail. */
function hikers(c: Ctx, d: Decor, t: number): void {
  const v = d.variant ?? 0, n = v % 4 === 3 ? 2 : 3;
  const facing = (['up', 'right', 'left', 'down'] as const)[v % 4];
  for (let i = 0; i < n; i++) {
    const look = HIKER_LOOKS[(i + v) % HIKER_LOOKS.length];
    const x = d.x - 12 + i * 13 + (i % 2) * 2, y = d.y + (i % 2) * 4 - 2;
    const seed = h1(i, v);
    const pose: Pose = { facing, phase: 0, moving: false, blink: (t * 0.8 + seed * 3) % 4 < 0.12, breath: (t * 0.5 + seed) % 1 };
    drawCharacter(c, look, x, y, pose);
    // Backpack (seen from behind) and the helmet dome over the hair.
    if (facing === 'up') block(c, x - 3, y - (look.small ? 16 : 20), 6, 7, i % 2 ? '#2f6fa8' : '#e2493b', undefined, undefined, '#1b1d24');
    const hy = y - (look.small ? 21 : 25) - (pose.breath > 0.5 ? 1 : 0);
    const col = HELMETS[(i + v) % HELMETS.length];
    c.fillStyle = col;
    c.beginPath();
    c.ellipse(x + 0.5, hy, 5.2, 3.6, 0, Math.PI, 0);
    c.fill();
    rect(c, shade(col, -0.25), x - 5, hy - 0.2, 11, 1.2);
    rect(c, 'rgba(255,255,255,0.5)', x - 2.5, hy - 2.6, 1.4, 1);
    if (i === 1 && facing !== 'up') {
      // A walking stick.
      rect(c, '#8a6a3a', x + 6, y - 14, 1, 14);
    }
  }
}

const WINGS = [['#2a6fb0', '#9fd3f0'], ['#ffd34e', '#1b1d24'], ['#e8a060', '#5a3a1a'], ['#9b5fc0', '#f4f1ea'], ['#1b1d24', '#4affc0']];

/** Butterflies fluttering around a sunny spot (Taroko's are famous), with flapping wings. */
function butterflies(c: Ctx, d: Decor, t: number): void {
  if (isNight()) return;
  const v = d.variant ?? 0, n = 3 + (v % 2);
  for (let i = 0; i < n; i++) {
    const a = t * (0.5 + h1(i, v) * 0.3) + i * 2.1;
    const bx = d.x + Math.sin(a) * 18 + Math.sin(a * 2.7) * 6;
    const by = d.y - 16 - Math.abs(Math.sin(a * 1.3)) * 14 + Math.sin(t * 6 + i) * 1.5;
    const [col, dot] = WINGS[(i + v) % WINGS.length];
    const flap = Math.abs(Math.sin(t * 14 + i * 2));
    const ww = 1.2 + flap * 2.6;
    ellipse(c, 'rgba(20,10,6,0.12)', bx, d.y + 1, 2, 0.6);
    ellipse(c, col, bx - ww * 0.8, by - 0.6, ww, 2);
    ellipse(c, col, bx + ww * 0.8, by - 0.6, ww, 2);
    ellipse(c, dot, bx - ww * 0.8, by - 0.8, ww * 0.35, 0.7);
    ellipse(c, dot, bx + ww * 0.8, by - 0.8, ww * 0.35, 0.7);
    rect(c, '#1b1d24', bx - 0.4, by - 2, 0.8, 3.4);
  }
}

/** An angler on the pebbles with a rod bent out over the surf, bucket beside him. */
function fisherman(c: Ctx, d: Decor, t: number): void {
  const v = d.variant ?? 0;
  const look: CharLook = v ? { skin: '#d9a47a', hair: '#1c1814', hairStyle: 'cap', hat: '#ffd34e', top: '#2f6fa8', topShade: '#1f4a7a', bottom: '#2f3442', shoes: '#1d1814' }
    : { skin: '#b8784a', hair: '#9a948a', hairStyle: 'cap', hat: '#f4f1ea', top: '#57a06a', topShade: '#3f7f52', bottom: '#5b4636', shoes: '#2b1d14' };
  const x = d.x, y = d.y;
  drawCharacter(c, look, x, y, { facing: 'right', phase: 0, moving: false, blink: (t + v) % 4 < 0.12, breath: (t * 0.4 + v) % 1 });
  // Rod, line and a float bobbing in the surf.
  const bend = Math.sin(t * 1.1 + v) * 1.5;
  line(c, '#3a2a20', 1, [x + 3, y - 12, x + 14, y - 30 + bend, x + 26, y - 36 + bend * 2]);
  line(c, 'rgba(255,255,255,0.7)', 0.4, [x + 26, y - 36 + bend * 2, x + 40 + Math.sin(t * 0.6) * 2, y - 2]);
  ellipse(c, '#e2493b', x + 40 + Math.sin(t * 0.6) * 2, y - 2 + Math.sin(t * 2.2) * 0.8, 1.2, 1.5);
  // Bucket and a cooler box.
  block(c, x - 12, y - 7, 6, 7, '#2f6fa8', '#4a8ac8', '#1f4a7a');
  ellipse(c, '#1f4a7a', x - 9, y - 7, 3, 1);
  block(c, x - 20, y - 6, 7, 6, '#f4f1ea', '#ffffff', '#c8c0b0');
  rect(c, '#e2493b', x - 20, y - 6.4, 7, 1.2);
}

/** A 飛魚 (flying-fish) stall: fish drying on a rack under a blue tarp, with the Qixingtan sign. */
function fishStall(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y;
  cachedArt(c, `tkFishStall:${isNight() ? 'n' : 'd'}`, x - 30, y - 52, 60, 58, (c) => {
    shadow(c, x, y, 24, 3.2, 0.32, 20);
    for (const px of [x - 24, x + 22]) rect(c, '#55606a', px, y - 38, 2, 38);
    // Tarp roof.
    poly(c, '#2f6fa8', [x - 28, y - 38, x + 28, y - 38, x + 25, y - 46, x - 25, y - 46]);
    for (let i = x - 26; i < x + 26; i += 6) rect(c, 'rgba(255,255,255,0.12)', i, y - 45.6, 3, 7.2);
    rect(c, '#1f4a7a', x - 28, y - 38.6, 56, 1);
    // Sign.
    block(c, x - 20, y - 54, 40, 9, '#e2493b', '#ff6a5a', '#8a2a1e');
    txt(c, '七星潭 飛魚乾', x, y - 49.6, 5, '#fff6dc', 900, 'rgba(0,0,0,0.35)');
    // Drying rack with fish.
    rect(c, '#8a6a3a', x - 22, y - 30, 44, 1.2);
    for (let i = 0; i < 7; i++) {
      const fx = x - 18 + i * 6;
      rect(c, '#55606a', fx, y - 30, 0.5, 3);
      ellipse(c, '#7ab0c8', fx, y - 22, 1.8, 5);
      ellipse(c, '#e8f0f4', fx + 0.6, y - 22, 0.9, 4);
      poly(c, '#5a8aa8', [fx - 1.5, y - 17, fx + 1.5, y - 17, fx, y - 19.5]);
      // "Wings".
      poly(c, 'rgba(60,90,120,0.7)', [fx, y - 24, fx - 3.5, y - 20, fx, y - 21]);
      poly(c, 'rgba(60,90,120,0.7)', [fx, y - 24, fx + 3.5, y - 20, fx, y - 21]);
    }
    // Counter with baskets.
    block(c, x - 24, y - 12, 48, 12, '#9a6a44', '#b88a5c', '#6e4a2e');
    for (let i = 0; i < 3; i++) {
      ellipse(c, '#c8a060', x - 14 + i * 14, y - 13, 5.5, 2.2);
      for (let k = 0; k < 4; k++) ellipse(c, '#7ab0c8', x - 17 + i * 14 + k * 2.2, y - 14, 1, 1.6);
    }
    txt(c, '飛魚·炸飛魚卵', x, y - 6, 3.2, '#fff6dc', 900);
  });
}

/** Beach-goers sitting on a mat on the pebbles, looking at the sea; a parasol over them. */
function beachFolk(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y, v = d.variant ?? 0;
  const mat = ['#ff9fc0', '#7ad0e0', '#ffd34e'][v % 3];
  // Parasol.
  if (v % 2 === 0) {
    rect(c, '#55606a', x + 14, y - 40, 1.2, 36);
    c.fillStyle = v % 4 ? '#e2493b' : '#2f6fa8';
    c.beginPath();
    c.moveTo(x - 6, y - 36);
    c.quadraticCurveTo(x + 14, y - 52, x + 34, y - 36);
    c.closePath();
    c.fill();
    for (let i = -4; i < 34; i += 8) poly(c, '#fff6e6', [x + i, y - 36.4, x + i + 3, y - 45 - Math.abs(i - 14) * 0.1, x + i + 6, y - 36.4]);
    ellipse(c, 'rgba(20,10,6,0.18)', x + 14, y + 2, 20, 5);
  }
  ellipse(c, 'rgba(20,10,6,0.2)', x + 2, y + 1, 16, 4);
  block(c, x - 14, y - 7, 32, 8, mat, shade(mat, 0.3), shade(mat, -0.25));
  const looks: CharLook[] = [
    { skin: '#f3cba6', hair: '#3a2418', hairStyle: 'long', top: '#ffffff', topShade: '#dcd6cc', bottom: '#3a8fd9', shoes: '#f4f1ea', dress: true },
    { skin: '#e8b48c', hair: '#1c1814', hairStyle: 'short', top: '#ff9f43', topShade: '#d07a2a', bottom: '#1b1d24', shoes: '#f4f1ea' },
  ];
  for (let i = 0; i < 2; i++) {
    const cx = x - 6 + i * 14;
    drawCharacter(c, looks[(i + v) % 2], cx, y - 3, { facing: 'right', phase: 0, moving: false, blink: (t * 0.7 + i + v) % 4 < 0.12, breath: (t * 0.5 + i * 0.4) % 1 });
  }
  // The mat's front edge covers their legs so they read as sitting.
  block(c, x - 14, y - 5, 32, 6, mat, shade(mat, 0.2), shade(mat, -0.25));
  // A cooler and a camera.
  block(c, x + 20, y - 6, 7, 6, '#f4f1ea', '#ffffff', '#c8c0b0');
  rect(c, '#3a8fd9', x + 20, y - 6.4, 7, 1.2);
}

/** A bleached driftwood log on the pebbles. */
function driftwood(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, v = d.variant ?? 0, w = 26 + v * 6;
  shadow(c, x, y, w / 2, 2.4, 0.3);
  c.save();
  c.translate(x, y - 3);
  c.rotate(v ? -0.12 : 0.08);
  block(c, -w / 2, -3.5, w, 6, '#c8b8a0', '#e4d8c4', '#8a7a62', '#5a4a3a');
  for (let i = -w / 2 + 3; i < w / 2 - 2; i += 5) rect(c, 'rgba(90,70,50,0.35)', i, -2.4 + (i % 2), w * 0.12, 0.6);
  ellipse(c, '#8a7a62', -w / 2 + 1, -0.5, 1.6, 2.2);
  line(c, '#a8987e', 1.6, [w / 2 - 2, -2, w / 2 + 5, -8]);
  c.restore();
}

function drawDecor(c: Ctx, d: Decor, t: number): boolean {
  switch (d.kind) {
    case 'tk_cimuBridge':
    case 'tk_redBridge':
      cimuBridge(c, d);
      return true;
    case 'tk_lion':
      lion(c, d);
      return true;
    case 'tk_rockWarn':
      rockWarning(c, d.x, d.y);
      return true;
    case 'tk_fallNet':
      fallNet(c, d);
      return true;
    case 'tk_swallowCliff':
      swallowCliff(c, d);
      return true;
    case 'tk_birds':
      birds(c, d, t);
      return true;
    case 'tk_rail':
      rail(c, d);
      return true;
    case 'tk_redRail':
      redRail(c, d);
      return true;
    case 'tk_bridgeRail':
      bridgeRail(c, d);
      return true;
    case 'tk_lantern':
      lantern(c, d);
      return true;
    case 'tk_trukuHouse':
      trukuHouse(c, d);
      return true;
    case 'tk_milletRack':
      milletRack(c, d);
      return true;
    case 'tk_firePit':
      firePit(c, d, t);
      return true;
    case 'tk_monkey':
      monkey(c, d, t);
      return true;
    case 'tk_boulder': {
      const v = d.variant ?? 0;
      boulder(c, d.x, d.y, [7, 5, 9][v], [8, 6, 9][v], true);
      if (v === 0) boulder(c, d.x + 8, d.y + 2, 3.4, 4, true);
      return true;
    }
    case 'tk_tourBus':
      tourBus(c, d);
      return true;
    case 'tk_bench':
      bench(c, d);
      return true;
    case 'tk_surf':
      surf(c, d, t);
      return true;
    case 'tk_boat':
      boatBeached(c, d, t);
      return true;
    case 'tk_boatSea':
      boatSea(c, d, t);
      return true;
    case 'tk_qixingStone':
      qixingStone(c, d);
      return true;
    case 'tk_cyclists':
      cyclists(c, d, t);
      return true;
    case 'tk_palm':
      palm(c, d, t);
      return true;
    case 'tk_sign':
      shopSign(c, d);
      return true;
    case 'tk_vcSign':
      vcSign(c, d);
      return true;
    case 'tk_parkStone':
      parkStone(c, d);
      return true;
    case 'tk_solid':
      return true;
    case 'tk_wall':
      wall(c, d);
      return true;
    case 'tk_eastCliff':
      eastCliff(c, d, t);
      return true;
    case 'tk_fern':
      fern(c, d);
      return true;
    case 'tk_dirSign':
      dirSign(c, d);
      return true;
    case 'tk_marbleWall':
      marbleWall(c, d);
      return true;
    case 'tk_mist':
      mist(c, d, t);
      return true;
    case 'tk_rapids':
      rapids(c, d, t);
      return true;
    case 'tk_glints':
      glints(c, d, t);
      return true;
    case 'tk_hikers':
      hikers(c, d, t);
      return true;
    case 'tk_butterflies':
      butterflies(c, d, t);
      return true;
    case 'tk_fisherman':
      fisherman(c, d, t);
      return true;
    case 'tk_fishStall':
      fishStall(c, d);
      return true;
    case 'tk_beachFolk':
      beachFolk(c, d, t);
      return true;
    case 'tk_driftwood':
      driftwood(c, d);
      return true;
    default:
      return false;
  }
}

registerPropDrawers({
  objects: OBJECTS,
  decor: drawDecor,
  animatedObjects: ['tk_shrine', 'tk_bell', 'tk_skip', 'tk_stargaze', 'tk_museum', 'tk_mochi', 'tk_sausage'],
  animatedDecor: ['tk_eastCliff', 'tk_birds', 'tk_firePit', 'tk_monkey', 'tk_surf', 'tk_boatSea', 'tk_cyclists', 'tk_palm', 'tk_boat', 'tk_mist', 'tk_rapids', 'tk_glints', 'tk_hikers', 'tk_butterflies', 'tk_fisherman', 'tk_beachFolk'],
});

